import { getFundDetailsByCode, nullFundDetails } from "../partials/fundDetails";
import moment from "moment";
import siteLanguage from "../partials/siteLanguage";
import { fundFacts } from "../partials/fundLinks";
import { drawChart } from "./chart-svg";

moment.locale(siteLanguage);

export default function fundDetail() {

	return {
		moment,
		...nullFundDetails,
		chart: "growth",
		fundFacts,
		/**
		 * @param {string} refinitivId
		 * @returns {Promise<FundDetails>}
		 */
		async fetchFund(refinitivId) {

			if (!refinitivId) {
				refinitivId = this.extractIdFromUrl();
			}

			this.refinitivId = refinitivId;
			Object.assign(this, await getFundDetailsByCode(refinitivId));

			// seems to need to be the next tick
			setTimeout(() => {
				this.resizeAndRedrawCharts();
			}, 0);

			let resizeTimeout;

			window.addEventListener("resize", () => {
				clearTimeout(resizeTimeout);
				resizeTimeout = setTimeout(() => this.resizeAndRedrawCharts(), 250);
			});
		},

		/**
		 * @description Used to retrieve the Refinitiv ID from the URL in the case this fund is not in Craft
		 * @returns {string}
		 */
		extractIdFromUrl() {
			const pathname = window.location.pathname;
			let id  = pathname.replace('/fund/', '');
			return id.substring(0, 8); // trim off any extra stuff
		},

		/**
		 * @param {Number} rawNumber
		 * @param {Number} significantFigures
		 * @returns {string}
		 */
		formatNumber(rawNumber, significantFigures = 2) {
			return Number(rawNumber.toFixed(significantFigures)).toLocaleString(
				siteLanguage,
				{
					minimumFractionDigits: significantFigures,
					maximumFractionDigits: significantFigures,
				}
			);
		},

		/**
		 * @param {Number} rawNumber
		 * @param {Number} significantFigures
		 * @returns {string}
		 */
		formatNumberWithoutRounding(rawNumber, significantFigures = 4) {
			return Number(rawNumber).toLocaleString(
				siteLanguage,
				{
					minimumFractionDigits: significantFigures,
					maximumFractionDigits: significantFigures,
				}
			);
		},

		/**
		 * Truncates a number without rounding to a given place.
		 *
		 * @param {Number} rawNumber
		 * @param {Number} place
		 */
		truncateNumber(rawNumber, place = 1e2) {
			// If the number has fewer decimal places than the or same as the given
			// place to truncate, abort and return the number as it was given.
			if (
				(rawNumber.toString().split(".")[1] || "").length <= Math.log10(place)
			) {
				return rawNumber;
			}

			return Math.trunc(rawNumber * place) / place;
		},
		/**
		 * @param {Number} rawPercentage
		 * @param {Number} significantFigures
		 * @returns {boolean|string}
		 */
		formatPercentage(rawPercentage, significantFigures = 2) {
			if (typeof rawPercentage !== "number") {
				return false;
			}

			return `${this.formatNumber(rawPercentage, significantFigures)}%`;
		},
		/**
		 * @param {Number} rawPrice
		 * @param {Number} significantFigures
		 * @param {string} unit - dollars or MM
		 * @returns {string}
		 */
		formatPrice(rawPrice, significantFigures = 2, unit = "dollars") {
			if (rawPrice === null) {
				return "–";
			}

			if (unit === "MM") {
				rawPrice = rawPrice / 1000000;
			}

			let response = this.formatNumber(
				this.truncateNumber(rawPrice, parseFloat(`1e${significantFigures}`)),
				significantFigures
			);

			if (siteLanguage === "en") {
				response = `$${response}`;
			} else if (siteLanguage === "fr") {
				response = `${response} $`;
			}

			return response;
		},

		/**
		 * @param {Number} rawPrice
		 * @param {Number} significantFigures
		 * @param {string} unit - dollars or MM
		 * @returns {string}
		 */
		formatNavPrice(rawPrice, significantFigures = 4, unit = "dollars") {
			if (rawPrice === null) {
				return "–";
			}

			if (unit === "MM") {
				rawPrice = rawPrice / 1000000;
			}

			let response = this.formatNumberWithoutRounding(rawPrice, significantFigures);

			if (siteLanguage === "en") {
				response = `$${response}`;
			} else if (siteLanguage === "fr") {
				response = `${response} $`;
			}

			return response;
		},


		/**
		 * @param {string} option - e.g. FE
		 * @param {string} series - e.g. Advisor
		 * @param {string} currency - e.g. US Dollar
		 * @param {bool} hedged
		 * @returns {PurchaseOption|null}
		 */
		getPurchaseOptionByOptionAndSeries(
			option,
			series,
			currency = "CAD",
			hedged = false
		) {
			return this.purchaseOptions.find(
				(purchaseOption) =>
					purchaseOption.purchaseOption === option &&
					purchaseOption.series === series &&
					purchaseOption.baseCurrency === currency &&
					purchaseOption.hedged === hedged
			);
		},

		/**
		 * @param {string} fundId - e.g. 68484352
		 * @returns {string}
		 */
		buildFundUrl(fundId) {
			return `/fund/${fundId}`;
		},

		/**
		 * @param {string} fundId - e.g. 68484352
		 * @returns {boolean}
		 */
		isCurrentFund(fundId) {
			return fundId === this.refinitivId
		},

		/**
		 * @returns {Number}
		 */
		getTopHoldingsTotalPercent() {
			if (this.topHoldings.length === 0) {
				return 0;
			}

			return this.topHoldings
				.map((topHolding) => topHolding.percentage)
				.reduce((carry, percent) => carry + percent);
		},
		/**
		 * Fit charts to their parent containers and redraw.
		 *
		 * `#chart0`, `#chart1`, `#chart2`, `#chart3` **must** be in DOM when this
		 * function is called.
		 *
		 * @returns {boolean}
		 */
		resizeAndRedrawCharts() {
			if (!(drawChart instanceof Function)) {
				return false;
			}
			/**
			 * Resize and redraw an individual chart.
			 *
			 * @param {HTMLElement} chartElement
			 * @returns {boolean}
			 */
			const resizeAndRedrawChart = (chartElement) => {
				chartElement.setAttribute(
					"mfhq-height",
					(chartElement.parentElement.clientWidth *
						chartElement.getAttribute("mfhq-height")) /
						chartElement.getAttribute("mfhq-width")
				);

				chartElement.setAttribute(
					"mfhq-width",
					chartElement.parentElement.clientWidth
				);

				drawChart(chartElement.id);

				return true;
			};

			["chart0", "chart1", "chart2", "chart3"]
				.map((chartId) => document.getElementById(chartId))
				.filter((chartElement) => chartElement.parentElement.clientWidth > 0)
				.filter(
					(chartElement) =>
						chartElement.parentElement.clientWidth !==
						Number(chartElement.getAttribute("mfhq-width"))
				)
				.forEach(resizeAndRedrawChart);

			return true;
		},
		/**
		 * @param {string} chart
		 * @returns {boolean}
		 */
		setChartTab(chart) {
			this.chart = chart;
			setTimeout(this.resizeAndRedrawCharts, 50);
			return true;
		},
		/**
		 * @param {string} unit
		 * @returns {Number}
		 */
		getSinceInception(unit = "years") {
			return Math.abs(
				moment(this.inceptionDate).diff(moment().startOf("day"), unit, true)
			);
		},
		/**
		 * @param {Number} performance
		 * @param {boolean} validPeriod
		 * @return {Number|string}
		 */
		displayPerformance(performance, validPeriod = false) {
			if (!validPeriod) {
				return "–";
			}

			return performance;
		},
		/**
		 * @param {Number} dist
		 * @param {Number} frequency
		 * @param {Number} nav
		 * @return {Number}
		 **/
		displayYield(dist, frequency, nav) {
			return ((dist * frequency) / nav) * 100;
		},

		/**
		 * @description A cleaner way to handle all of this
		 * @param {Number} projectedYield
		 * @return {string|boolean}
		 **/
		getYieldDisplay(projectedYield) {
			// turn to a percentage
			const percentage = projectedYield * 100;

			// format
			const formattedDisplay = this.formatPercentage(percentage, 2);

			return formattedDisplay ? formattedDisplay : "–";
		}
	}
}
